import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppFrame } from './components/AppFrame';
import { LoginPage } from './routes/LoginPage';
import { SignupPage } from './routes/SignupPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { AppLayout } from './routes/app/AppLayout';
import { DashboardPage } from './routes/DashboardPage';
import { GalleryPage } from './routes/GalleryPage';
import { AddItemPage } from './routes/AddItemPage';
import { EditItemPage } from './routes/EditItemPage';
import { ItemDetailPage } from './routes/ItemDetailPage';
import { BoxesPage } from './routes/app/BoxesPage';
import { BoxDetailPage } from './routes/app/BoxDetailPage';
import { MorePage } from './routes/app/MorePage';
import { NeedsDecisionPage } from './routes/app/NeedsDecisionPage';
import { RoomProgressPage } from './routes/app/RoomProgressPage';
import { IconAddPage } from './routes/app/IconAddPage';
import { RequireEdit } from './components/app/RequireEdit';
import { RouteErrorBoundary } from './components/app/RouteErrorBoundary';

/**
 * Route map for V1 — the app's slice of the shared origin:
 *   "/"            the prerendered landing site (apps/landing) — served as
 *                  static files by hosting/the dev middleware, NOT by this SPA
 *   "/login|signup" public auth
 *   "/app/*"       auth-gated app (bottom-nav layout)
 *
 * The everyday surfaces (dashboard, gallery, add/edit, boxes) stay in the main
 * chunk; secondary tools below use route-level `lazy` so their code (e.g. the
 * QR scanner with jsqr, the print pages, sharing) loads on demand.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppFrame />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Reachable only via in-app history (prod serves the static landing).
      { index: true, element: <Navigate to="/app" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'signup', element: <SignupPage /> },

      {
        path: 'app',
        element: <AppLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'items', element: <GalleryPage /> },
          { path: 'items/new', element: <RequireEdit><IconAddPage /></RequireEdit> },
          { path: 'items/new/details', element: <RequireEdit><AddItemPage /></RequireEdit> },
          { path: 'capture', element: <Navigate to="/app/items/new?mode=photos" replace /> },
          { path: 'items/:itemId', element: <ItemDetailPage /> },
          { path: 'items/:itemId/edit', element: <RequireEdit><EditItemPage /></RequireEdit> },
          { path: 'boxes', element: <BoxesPage /> },
          { path: 'boxes/:boxId', element: <BoxDetailPage /> },
          { path: 'more', element: <MorePage /> },
          { path: 'needs', element: <NeedsDecisionPage /> },
          { path: 'rooms', element: <RoomProgressPage /> },

          // --- lazy: secondary tools (own chunks) ---
          {
            path: 'boxes/labels',
            lazy: async () => ({
              Component: (await import('./routes/app/BoxLabelsPage')).BoxLabelsPage,
            }),
          },
          {
            path: 'scan',
            lazy: async () => ({ Component: (await import('./routes/app/ScanPage')).ScanPage }),
          },
          {
            path: 'export',
            lazy: async () => ({ Component: (await import('./routes/app/ExportPage')).ExportPage }),
          },
          {
            path: 'report',
            lazy: async () => ({
              Component: (await import('./routes/app/MoveReportPage')).MoveReportPage,
            }),
          },
          {
            path: 'taxonomy',
            lazy: async () => {
              const { TaxonomyManagePage } = await import('./routes/app/TaxonomyManagePage');
              return { Component: () => <RequireEdit><TaxonomyManagePage /></RequireEdit> };
            },
          },
          {
            path: 'move',
            lazy: async () => {
              const { MoveWizardPage } = await import('./routes/app/MoveWizardPage');
              return { Component: () => <RequireEdit><MoveWizardPage /></RequireEdit> };
            },
          },
          {
            path: 'settle-in',
            lazy: async () => ({
              Component: (await import('./routes/app/SettleInPage')).SettleInPage,
            }),
          },
          {
            path: 'share',
            lazy: async () => ({ Component: (await import('./routes/app/SharePage')).SharePage }),
          },
          {
            path: 'settings',
            lazy: async () => ({
              Component: (await import('./routes/app/SettingsPage')).SettingsPage,
            }),
          },
          {
            path: 'progress',
            lazy: async () => ({
              Component: (await import('./routes/app/ProgressPage')).ProgressPage,
            }),
          },
        ],
      },

      { path: '404', element: <NotFoundPage /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
]);
